import React, { useEffect } from "react"
import { Spinner } from "../../components/shared/Spinner"
import { RequestError } from "../../components/shared/RequestError"
import { useMutation } from "react-query"
import { mutationRequest } from "../../util/request"
import { usePayee } from "../../hooks/usePayee"

interface PayeeSectionProps {
  payeeId: number
}

interface Payee {
  status: string
  stripe_onboarding_url?: string | null
  is_stripe_onboarding_url_expired?: boolean
  stripe_login_url?: string
}

interface MutationResponse {
  success: boolean
  payee?: Payee
}

export const PayeeSection: React.FC<PayeeSectionProps> = ({ payeeId }) => {
  const { status, data, error } = usePayee(payeeId)
  const payee: Payee | null = status === "success" && data ? data.payee : null

  const createLinkMutation = useMutation<MutationResponse, Error, any>(mutationRequest)
  // Separate from createLinkMutation so its success/error handling below
  // can't fire the onboarding-link redirect/reload for this unrelated action.
  const markLoginUrlUsedMutation = useMutation<MutationResponse, Error, any>(mutationRequest)

  const handleCreateAccountLink = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    createLinkMutation.mutate({
      options: { method: "PUT" },
      path: `/api/v1/payees/${payeeId}`,
      data: { payee: { create_stripe_account_link: true } },
    })
  }

  // Stripe login links are single-use, so mark this one used in the
  // background once clicked - the anchor's own href/target="_blank" already
  // handles taking the user to Stripe, so this must not block or redirect.
  const handleStripeLoginUrlClick = () => {
    markLoginUrlUsedMutation.mutate({
      options: { method: "PUT" },
      path: `/api/v1/payees/${payeeId}`,
      data: { payee: { stripe_login_url_used: true } },
    })
  }

  useEffect(() => {
    if (createLinkMutation.status === "success" && createLinkMutation.data) {
      if (createLinkMutation.data.success && createLinkMutation.data.payee?.stripe_onboarding_url) {
        window.location.href = createLinkMutation.data.payee.stripe_onboarding_url
      } else {
        console.error("Failed to create stripe account link: ", createLinkMutation.data)
        window.location.reload()
      }
    } else if (createLinkMutation.status === "error") {
      console.error(
        "Mutation failed to create stripe account link: ",
        createLinkMutation.error,
      )
      window.location.reload()
    }
  }, [createLinkMutation.status, createLinkMutation.data, createLinkMutation.error])

  return (
    <div className="mb-4 text-base">
      {status === "loading" ? (
        <Spinner />
      ) : status === "error" ? (
        <RequestError error={error} />
      ) : payee?.status === "initialized" ? (
        payee.stripe_onboarding_url &&
        !payee.is_stripe_onboarding_url_expired ? (
          <a
            className="text-blue-600"
            href={payee.stripe_onboarding_url}
            target="_blank"
            rel="noreferrer"
          >
            Setup Your Stripe Account
          </a>
        ) : (
          <>
            <button
              className="text-blue-600 focus:outline-none lg:text:base text:sm"
              onClick={handleCreateAccountLink}
            >
              Setup Your Stripe Account
            </button>
            <span className="text-gray-600 pl-2 text-sm">
              you need to do this (only once) to get paid
            </span>
          </>
        )
      ) : payee?.stripe_login_url ? (
        <a
          className="text-blue-600"
          href={payee.stripe_login_url}
          onClick={handleStripeLoginUrlClick}
          target="_blank"
          rel="noreferrer"
        >
          Your Stripe Payout Dashboard
        </a>
      ) : null}
    </div>
  )
}
